import { useCallback, useState } from 'react';

export const SIDEBAR_FAVORITES_STORAGE_KEY = 'backstage.sidebar.favorites.v2';
export const SIDEBAR_SECTIONS_STORAGE_KEY = 'backstage.sidebar.sections.v1';

export type SidebarDropTarget = { section: number; before: string | null };
type SidebarLayout = { favorites: string[]; sections: Record<string, number> };

export const normalizeSidebarPath = (path: string) => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return normalized === '/agentic-review' ? '/make-review' : normalized;
};

const uniquePaths = (paths: string[]) => [
  ...new Set(paths.map(normalizeSidebarPath)),
];

const readFavorites = (): string[] | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const stored = window.localStorage.getItem(SIDEBAR_FAVORITES_STORAGE_KEY);
    if (stored === null) {
      return undefined;
    }

    const parsed = JSON.parse(stored);
    return Array.isArray(parsed)
      ? uniquePaths(
          parsed.filter((value): value is string => typeof value === 'string'),
        )
      : undefined;
  } catch {
    return undefined;
  }
};

const readSections = (
  favorites: string[],
): Record<string, number> | undefined => {
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(SIDEBAR_SECTIONS_STORAGE_KEY) ?? 'null',
    );
    // Discard stale metadata if only one of the two storage writes succeeded.
    if (
      !stored ||
      !Array.isArray(stored.favorites) ||
      stored.favorites.some((path: unknown) => typeof path !== 'string') ||
      JSON.stringify(uniquePaths(stored.favorites)) !==
        JSON.stringify(favorites) ||
      !stored.sections ||
      typeof stored.sections !== 'object' ||
      Array.isArray(stored.sections)
    ) {
      return undefined;
    }
    return Object.fromEntries(
      Object.entries(stored.sections)
        .map(([path, section]): [string, unknown] => [
          normalizeSidebarPath(path),
          section,
        ])
        .filter(
          (entry): entry is [string, number] =>
            favorites.includes(entry[0]) &&
            typeof entry[1] === 'number' &&
            Number.isInteger(entry[1]) &&
            entry[1] >= 0,
        ),
    );
  } catch {
    return undefined;
  }
};

const writeLayout = (layout: SidebarLayout) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      SIDEBAR_FAVORITES_STORAGE_KEY,
      JSON.stringify(layout.favorites),
    );
    window.localStorage.setItem(
      SIDEBAR_SECTIONS_STORAGE_KEY,
      JSON.stringify(layout),
    );
  } catch {
    // A disabled or full localStorage should not break navigation.
  }
};

export const useSidebarFavorites = (
  defaultFavorites: string[] = [],
  defaultSections: string[][] = [[]],
  {
    initialAdditions = [],
    fallbackSection = defaultSections.length - 1,
  }: { initialAdditions?: string[]; fallbackSection?: number } = {},
) => {
  const [layout, setLayout] = useState<SidebarLayout>(() => {
    const storedFavorites = readFavorites();
    const favorites = uniquePaths([
      ...initialAdditions.filter(path => !storedFavorites?.includes(path)),
      ...(storedFavorites ?? defaultFavorites),
    ]);
    const storedSections = storedFavorites && readSections(storedFavorites);
    if (storedSections) return { favorites, sections: storedSections };

    // Older layouts had fixed divider slots. Preserve the visible arrangement
    // on migration, then let each section grow or shrink as items move.
    const slots = defaultSections.flatMap((paths, section) =>
      paths.filter(path => favorites.includes(path)).map(() => section),
    );
    const available = new Set(defaultSections.flat());
    const sections = Object.fromEntries(
      favorites
        .filter(path => available.has(path))
        .map((path, index) => [path, slots[index]]),
    );
    return { favorites, sections };
  });

  const getSection = useCallback(
    (path: string, sections = layout.sections) => {
      const defaultSection = defaultSections.findIndex(paths =>
        paths.includes(path),
      );
      return Math.min(
        sections[path] ??
          (defaultSection < 0 ? fallbackSection : defaultSection),
        defaultSections.length - 1,
      );
    },
    [defaultSections, fallbackSection, layout.sections],
  );

  // Keep unavailable paths: temporarily disabling a feature must not erase
  // its position. The stored array also gives existing favorites a stable order.
  const moveFavorite = useCallback(
    (path: string, { before, section }: SidebarDropTarget) => {
      setLayout(current => {
        if (path === before && getSection(path, current.sections) === section) {
          return current;
        }
        const sections = { ...current.sections, [path]: section };
        const favorites = current.favorites.filter(
          favorite => favorite !== path,
        );
        const index =
          before === null
            ? favorites.findIndex(
                favorite => getSection(favorite, sections) > section,
              )
            : favorites.indexOf(before);
        favorites.splice(index < 0 ? favorites.length : index, 0, path);
        const next = { favorites, sections };
        writeLayout(next);
        return next;
      });
    },
    [getSection],
  );

  const removeFavorite = useCallback((path: string) => {
    setLayout(current => {
      const sections = { ...current.sections };
      delete sections[path];
      const next = {
        favorites: current.favorites.filter(favorite => favorite !== path),
        sections,
      };
      writeLayout(next);
      return next;
    });
  }, []);

  const resetFavorites = useCallback(() => {
    const favorites = uniquePaths(defaultFavorites);
    const sections = Object.fromEntries(
      favorites.flatMap(path => {
        const section = defaultSections.findIndex(paths =>
          paths.includes(path),
        );
        return section < 0 ? [] : [[path, section]];
      }),
    );
    const next = { favorites, sections };
    writeLayout(next);
    setLayout(next);
  }, [defaultFavorites, defaultSections]);

  return {
    favorites: layout.favorites,
    getSection,
    moveFavorite,
    removeFavorite,
    resetFavorites,
  };
};
